import { useMemo } from 'react';
import { useAuthUser } from './AuthContext';
import { useOrganization, Organization } from './OrganizationContext';
import { useViewAs } from './ViewAsContext';
import { usePermissions } from './PermissionContext';
import { useNavigation } from './NavigationContext';
import { type GlobalRole, type ProjectRole } from '@aica/core/rbac';
import type { AccessState } from '@/lib/route-permissions';

/**
 * Role Adapter - Backward Compatibility Layer
 * Composites 4 separated contexts into the old RoleContext API
 * This allows gradual migration without breaking existing components
 */

// State Machine types for backward compatibility
type RoleState =
  | { status: 'initializing' }
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'ready' };

interface RoleContextValue {
  // User info (from AuthContext)
  userId: string | null;
  email: string | null;
  name: string | null;

  // Organizations (from OrganizationContext)
  orgs: Organization[];
  selectedOrg: Organization | null;
  selectedOrgId: string | null;
  selectOrg: (orgId: string) => void;
  isOrgSwitching: boolean;

  // Current org role (from PermissionContext)
  globalRole: GlobalRole | null;
  projectRoles: Map<string, ProjectRole>;

  // Platform super-admin flag (can access admin panel, view all orgs)
  isOwner: boolean;

  // `/access/me` (E1.5, #1645) — see PermissionContext.
  access: AccessState;

  // Owner Mode - View As functionality (from ViewAsContext)
  viewAsRole: GlobalRole | null;
  setViewAsRole: (role: GlobalRole | null) => void;
  isViewAsMode: boolean;

  // State Machine
  roleState: RoleState;

  // Backward compatibility helpers
  isLoading: boolean;
  error: string | null;

  // Permission checks (from PermissionContext)
  canAccess: (requiredRole: GlobalRole | GlobalRole[]) => boolean;
  canManageProjects: boolean;
  canCreateProject: boolean;
  canSeeBilling: boolean;
  canManageUsers: boolean;
  canListUsers: boolean;
  canUploadRecords: boolean;
  canManageAlerts: boolean;

  // Project-specific permissions (from PermissionContext)
  // BREAKING CHANGE: now requires projectOrgId for security
  canEditProject: (projectId: string, projectOrgId: string) => boolean;
  canDeleteProject: (projectId: string, projectOrgId: string) => boolean;
  canViewProject: (projectId: string) => boolean;

  // Navigation (from NavigationContext)
  getLandingPage: () => string;
  getVisibleScreens: () => string[];

  // Project role loading (from PermissionContext)
  loadProjectRole: (projectId: string) => Promise<void>;
}

/**
 * Adapter Hook - Composites all 4 contexts into old API
 * This is the new useRole() implementation
 */
export function useRole(): RoleContextValue {
  const auth = useAuthUser();
  const org = useOrganization();
  const viewAs = useViewAs();
  const permissions = usePermissions();
  const navigation = useNavigation();

  // Composite state machine from multiple contexts
  const roleState: RoleState = useMemo(() => {
    // Check if any context is loading
    if (auth.isLoading || org.isLoading) {
      return { status: 'loading' };
    }

    // Check if any context has error
    if (auth.error) {
      return { status: 'error', error: auth.error };
    }
    if (org.error) {
      return { status: 'error', error: org.error };
    }

    // All ready
    return { status: 'ready' };
  }, [auth.isLoading, auth.error, org.isLoading, org.error]);

  // Backward compatibility helpers
  const isLoading = roleState.status === 'loading';
  const error = roleState.status === 'error' ? roleState.error : null;

  return {
    // Auth
    userId: auth.user?.userId || null,
    email: auth.user?.email || null,
    name: auth.user?.name || null,

    // Organization
    orgs: org.organizations,
    selectedOrg: org.currentOrganization,
    selectedOrgId: org.selectedOrgId,
    selectOrg: org.selectOrganization,
    isOrgSwitching: org.isOrgSwitching,

    // Permissions
    globalRole: permissions.globalRole,
    projectRoles: permissions.projectRoles,
    isOwner: permissions.isOwner,
    access: permissions.access,

    // Owner Mode - View As
    viewAsRole: viewAs.viewAsRole,
    setViewAsRole: viewAs.setViewAsRole,
    isViewAsMode: viewAs.isViewAsMode,

    // Permission checks
    canAccess: permissions.canAccess,
    canManageProjects: permissions.canManageProjects,
    canCreateProject: permissions.canCreateProject,
    canSeeBilling: permissions.canSeeBilling,
    canManageUsers: permissions.canManageUsers,
    canListUsers: permissions.canListUsers,
    canUploadRecords: permissions.canUploadRecords,
    canManageAlerts: permissions.canManageAlerts,
    canEditProject: permissions.canEditProject,
    canDeleteProject: permissions.canDeleteProject,
    canViewProject: permissions.canViewProject,
    loadProjectRole: permissions.loadProjectRole,

    // Navigation
    getLandingPage: navigation.getLandingPage,
    getVisibleScreens: navigation.getVisibleScreens,

    // State
    roleState,
    isLoading,
    error,
  };
}
